import { MultiSelect, useToast } from '@ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormDialog } from '../../../app/FormDialog';
import { errorMessage } from '../../../lib/errors';
import {
  useReferrers,
  useSetDaySellers,
  useSetMarketSellers,
  type Market,
  type MarketDay,
} from '../api';

export type SellersDialogProps =
  | { market: Market; day?: undefined; onClose: () => void }
  | { day: MarketDay; market?: undefined; onClose: () => void };

/** Chooses the sellers of a market (standard) or of one market day (MKT-015). */
export function SellersDialog({ market, day, onClose }: SellersDialogProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const members = useReferrers();
  const setMarket = useSetMarketSellers();
  const setDay = useSetDaySellers();
  const current = (market ?? day)?.sellers ?? [];
  const [selected, setSelected] = useState(current.map((s) => s.id));
  const mutation = market ? setMarket : setDay;
  // Former members that are still listed stay selectable, so they can be removed.
  const options = [
    ...(members.data ?? []).map((m) => ({ id: m.id, label: m.name })),
    ...current
      .filter((s) => !(members.data ?? []).some((m) => m.id === s.id))
      .map((s) => ({ id: s.id, label: t('sales.referrer.former', { name: s.name }) })),
  ];
  const summary =
    selected.length === 0
      ? t('sales.sellers.none')
      : options
          .filter((o) => selected.includes(o.id))
          .map((o) => o.label)
          .join(', ');
  return (
    <FormDialog
      open
      onClose={onClose}
      size="sm"
      title={t('sales.sellers.dialogTitle')}
      submitLabel={t('common.save')}
      pending={mutation.isPending}
      error={mutation.error ? errorMessage(mutation.error) : undefined}
      onSubmit={() => {
        const done = {
          onSuccess: () => {
            toast.show(t('sales.sellers.saved'));
            onClose();
          },
        };
        if (market) setMarket.mutate({ market: market.id, sellerIds: selected }, done);
        else if (day) setDay.mutate({ day: day.id, sellerIds: selected }, done);
      }}
    >
      <MultiSelect
        label={t('sales.sellers.title')}
        title={t('sales.sellers.pickerTitle')}
        summary={summary}
        options={options}
        selected={selected}
        onChange={setSelected}
        empty={t('sales.sellers.noMembers')}
        labels={{
          search: t('sales.sellers.pickerSearch'),
          clearSearch: t('common.clear'),
          noMatch: t('common.noResults'),
          countOf: (count, total) => t('apiary.picker.countOf', { count, total }),
          selected: (count) => t('common.selected', { count }),
          selectAll: t('apiary.picker.selectAll'),
          selectNone: t('apiary.picker.selectNone'),
          done: t('apiary.picker.done'),
          cancel: t('common.cancel'),
          close: t('common.close'),
        }}
      />
    </FormDialog>
  );
}
